import { auth } from '../firebase';
import { getAuth } from 'firebase/auth';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://klorah-fast-server-my8l.onrender.com/api';
// const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api'; // Testing with local server
const defaultHeaders = {
  'Content-Type': 'application/json',
  Accept: 'application/json'
};

class EmailService {
  static BASE_URL = BASE_URL;

  static async checkGmailAuth(userId) {
    console.log('🔍 Checking Gmail auth status...');
    const currentUser = auth.currentUser;
    if (!currentUser || !userId) {
      console.log('❌ No current user or userId found');
      return { authorized: false, email: null };
    }
    const idToken = await currentUser.getIdToken();
    const response = await fetch(`${BASE_URL}/users/${userId}/profile`, {
      method: 'GET',
      headers: {
        ...defaultHeaders,
        Authorization: `Bearer ${idToken}`
      }
    });
    if (!response.ok) {
      console.log('❌ Failed to check auth status');
      return { authorized: false, email: null };
    }
    const data = await response.json();
    if (!data.success || !data.email) {
      console.log('❌ Auth check unsuccessful');
      return { authorized: false, email: null };
    }
    console.log('✅ Gmail auth check successful');
    return { authorized: true, email: data.email };
  }

  static async initiateGmailAuth(userId) {
    console.log('🔑 Initiating Gmail authorization...');
    const response = await fetch(`${BASE_URL}/oauth2/gmail/authorize?user_id=${encodeURIComponent(userId)}`, {
      method: 'POST',
      headers: defaultHeaders
    });
    if (!response.ok) {
      console.log('❌ Failed to initiate Gmail auth');
      throw new Error('Failed to initiate Gmail auth');
    }
    const data = await response.json();
    if (!data.authUrl) {
      console.log('❌ No auth URL received');
      throw new Error('No authUrl in response');
    }
    console.log('🔄 Redirecting to Gmail auth page...');
    window.location.href = data.authUrl;
  }

  static async getAccounts(userId) {
    console.log('📧 Fetching email accounts...');
    const currentUser = auth.currentUser;
    if (!currentUser || !userId) {
      console.log('❌ No current user or userId found');
      return { accounts: [] };
    }
    const idToken = await currentUser.getIdToken();
    const response = await fetch(`${BASE_URL}/users/${userId}/accounts`, {
      method: 'GET',
      headers: {
        ...defaultHeaders,
        Authorization: `Bearer ${idToken}`
      }
    });
    if (!response.ok) {
      console.log('❌ Failed to fetch accounts');
      return { accounts: [] };
    }
    console.log('✅ Successfully fetched accounts');
    return await response.json();
  }

  static async getEmails(userId, accountId, options = {}) {
    console.log('📨 Fetching emails...');
    const currentUser = auth.currentUser;
    if (!currentUser || !userId) {
      console.log('❌ No current user or userId found');
      return { emails: [], nextPageToken: null, hasMore: false };
    }
    const idToken = await currentUser.getIdToken();
    const queryParams = new URLSearchParams();
    if (options.pageToken) {
      queryParams.append('page_token', options.pageToken);
    }
    if (accountId) {
      queryParams.append('account_id', accountId);
    }
    if (options.view) {
      queryParams.append('folder', options.view);
    }
    if (options.q) {
      queryParams.append('q', options.q);
      console.log('🔍 Search query:', options.q);
    }
    const limit = options.limit || 10;
    queryParams.append('limit', String(limit));
    const url = `${BASE_URL}/emails/${userId}?${queryParams.toString()}`;
    console.log('🌐 API URL:', url);
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        ...defaultHeaders,
        Authorization: `Bearer ${idToken}`
      }
    });
    if (!res.ok) {
      const errText = await res.text();
      console.log('❌ Failed to fetch emails');
      return { error: `Failed to fetch emails: ${res.status} - ${errText}` };
    }
    console.log('✅ Successfully fetched emails');
    return await res.json();
  }

  // ----------------------------------------------
  // NEW METHOD: Fetch the full content of an email
  // ----------------------------------------------
  static async getEmailContent(userId, messageId, accountId) {
    console.log('📜 Fetching full email content...');
    
    // Validate accountId first
    if (!accountId) {
      console.error('❌ No account ID provided when fetching email content');
      throw new Error('Missing account ID. Please select an account and try again.');
    }
    
    // Validate userId and current user
    const currentUser = auth.currentUser;
    if (!currentUser || !userId) {
      console.error('❌ No current user or userId found');
      throw new Error('Authentication required. Please sign in and try again.');
    }
    
    // Validate messageId
    if (!messageId) {
      console.error('❌ No message ID provided when fetching email content');
      throw new Error('Missing email ID. Please select an email and try again.');
    }
    
    try {
      const idToken = await currentUser.getIdToken();
      
      console.log(`Fetching email with ID ${messageId} from account ${accountId}`);
      const url = `${BASE_URL}/emails/${userId}/${messageId}?account_id=${accountId}`;
      
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          ...defaultHeaders,
          Authorization: `Bearer ${idToken}`
        }
      });
      
      if (!res.ok) {
        const errText = await res.text();
        console.log('❌ Failed to fetch email content:', res.status, errText);
        
        // Provide more specific error message for common cases
        if (res.status === 404) {
          throw new Error('Email not found. It may have been deleted or may not be accessible in this account.');
        } else if (res.status === 401 || res.status === 403) {
          throw new Error('Not authorized to view this email. Please check your account permissions.');
        } else {
          throw new Error(`Failed to fetch email content: ${res.status} - ${errText}`);
        }
      }
      
      const data = await res.json();
      console.log('✅ Full email content retrieved');
      return data; // {id, snippet, payload: { html, text }, ...}
    } catch (error) {
      console.error('❌ Error in getEmailContent:', error);
      throw error;
    }
  }

  // ----------------------------------------------
  // NEW METHOD: Get thread messages for an email
  // ----------------------------------------------
  static async getThread(userId, threadId, accountId) {
    console.log('🧵 Fetching email thread...');
    
    // Validation
    if (!accountId) {
      console.error('❌ No account ID provided when fetching thread');
      throw new Error('Missing account ID. Please select an account and try again.');
    }
    
    const currentUser = auth.currentUser;
    if (!currentUser || !userId) {
      console.error('❌ No current user or userId found');
      throw new Error('Authentication required. Please sign in and try again.');
    }
    
    if (!threadId) {
      console.error('❌ No thread ID provided when fetching thread');
      throw new Error('Missing thread ID. Cannot load thread messages.');
    }
    
    try {
      const idToken = await currentUser.getIdToken();
      
      console.log(`Fetching thread with ID ${threadId} from account ${accountId}`);
      const url = `${BASE_URL}/threads/${userId}/${threadId}?account_id=${accountId}`;
      
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          ...defaultHeaders,
          Authorization: `Bearer ${idToken}`
        }
      });
      
      if (!res.ok) {
        const errText = await res.text();
        console.log('❌ Failed to fetch thread:', res.status, errText);
        
        // Provide more specific error message for common cases
        if (res.status === 404) {
          throw new Error('Thread not found. It may have been deleted or may not be accessible.');
        } else if (res.status === 401 || res.status === 403) {
          throw new Error('Not authorized to view this thread. Please check your account permissions.');
        } else {
          throw new Error(`Failed to fetch thread: ${res.status} - ${errText}`);
        }
      }
      
      const data = await res.json();
      console.log('✅ Full thread retrieved');
      return data; // { messages: [...] }
    } catch (error) {
      console.error('❌ Error in getThread:', error);
      throw error;
    }
  }

  // ----------------------------------------------
  // NEW METHOD: Summarize email content using AI
  // ----------------------------------------------
  static async summarizeEmail(emailContent) {
    console.log('🤖 Generating AI summary for email...');
    
    const response = await fetch(`${BASE_URL}/ai-summarize-email`, {
      method: 'POST',
      headers: defaultHeaders,
      body: JSON.stringify({ email_content: emailContent })
    });
    
    if (!response.ok) {
      const errText = await response.text();
      console.log('❌ Failed to generate email summary');
      throw new Error(`Failed to generate email summary: ${response.status} - ${errText}`);
    }
    
    const data = await response.json();
    console.log('✅ Email summary generated successfully');
    return data; // { summary: "..." }
  }

  // ----------------------------------------------
  // NEW METHOD: Mark an email as read
  // ----------------------------------------------
  static async markEmailAsRead(userId, messageId, accountId) {
    console.log('✓ Marking email as read...');
    const currentUser = auth.currentUser;
    if (!currentUser || !userId) {
      throw new Error('No current user or userId found');
    }
    const idToken = await currentUser.getIdToken();

    const url = `${BASE_URL}/emails/${userId}/${messageId}/read`;
    const queryParams = new URLSearchParams();
    if (accountId) {
      queryParams.append('account_id', accountId);
    }

    const response = await fetch(`${url}?${queryParams.toString()}`, {
      method: 'POST',
      headers: {
        ...defaultHeaders,
        Authorization: `Bearer ${idToken}`
      }
    });

    if (!response.ok) {
      const errText = await response.text();
      console.log('❌ Failed to mark email as read');
      throw new Error(`Failed to mark email as read: ${response.status} - ${errText}`);
    }

    console.log('✅ Email marked as read successfully');
    return await response.json();
  }

  // ----------------------------------------------
  // Mark email as unread
  // ----------------------------------------------
  static async markEmailAsUnread(userId, messageId, accountId) {
    console.log('📬 Marking email as unread...');
    const currentUser = auth.currentUser;
    if (!currentUser || !userId) {
      throw new Error('No current user or userId found');
    }
    const idToken = await currentUser.getIdToken();

    const url = `${BASE_URL}/emails/${userId}/${messageId}/unread`;
    const queryParams = new URLSearchParams();
    if (accountId) {
      queryParams.append('account_id', accountId);
    }

    const response = await fetch(`${url}?${queryParams.toString()}`, {
      method: 'POST',
      headers: {
        ...defaultHeaders,
        Authorization: `Bearer ${idToken}`
      }
    });

    if (!response.ok) {
      const errText = await response.text();
      console.log('❌ Failed to mark email as unread');
      throw new Error(`Failed to mark email as unread: ${response.status} - ${errText}`);
    }

    console.log('✅ Email marked as unread successfully');
    return await response.json();
  }

  // ----------------------------------------------
  // Archive email
  // ----------------------------------------------
  static async archiveEmail(userId, messageId, accountId) {
    console.log('📦 Archiving email...');
    const currentUser = auth.currentUser;
    if (!currentUser || !userId) {
      throw new Error('No current user or userId found');
    }
    const idToken = await currentUser.getIdToken();

    const url = `${BASE_URL}/emails/${userId}/${messageId}/archive`;
    const queryParams = new URLSearchParams();
    if (accountId) {
      queryParams.append('account_id', accountId);
    }

    const response = await fetch(`${url}?${queryParams.toString()}`, {
      method: 'POST',
      headers: {
        ...defaultHeaders,
        Authorization: `Bearer ${idToken}`
      }
    });

    if (!response.ok) {
      const errText = await response.text();
      console.log('❌ Failed to archive email');
      throw new Error(`Failed to archive email: ${response.status} - ${errText}`);
    }

    console.log('✅ Email archived successfully');
    return await response.json();
  }

  // ----------------------------------------------
  // Unarchive email
  // ----------------------------------------------
  static async unarchiveEmail(userId, messageId, accountId) {
    console.log('📤 Unarchiving email...');
    const currentUser = auth.currentUser;
    if (!currentUser || !userId) {
      throw new Error('No current user or userId found');
    }
    const idToken = await currentUser.getIdToken();

    const url = `${BASE_URL}/emails/${userId}/${messageId}/unarchive`;
    const queryParams = new URLSearchParams();
    if (accountId) {
      queryParams.append('account_id', accountId);
    }

    const response = await fetch(`${url}?${queryParams.toString()}`, {
      method: 'POST',
      headers: {
        ...defaultHeaders,
        Authorization: `Bearer ${idToken}`
      }
    });

    if (!response.ok) {
      const errText = await response.text();
      console.log('❌ Failed to unarchive email');
      throw new Error(`Failed to unarchive email: ${response.status} - ${errText}`);
    }

    console.log('✅ Email unarchived successfully');
    return await response.json();
  }

  // ----------------------------------------------
  // Move email to trash
  // ----------------------------------------------
  static async trashEmail(userId, messageId, accountId) {
    console.log('🗑️ Moving email to trash...');
    const currentUser = auth.currentUser;
    if (!currentUser || !userId) {
      throw new Error('No current user or userId found');
    }
    const idToken = await currentUser.getIdToken();

    const url = `${BASE_URL}/emails/${userId}/${messageId}/trash`;
    const queryParams = new URLSearchParams();
    if (accountId) {
      queryParams.append('account_id', accountId);
    }

    const response = await fetch(`${url}?${queryParams.toString()}`, {
      method: 'POST',
      headers: {
        ...defaultHeaders,
        Authorization: `Bearer ${idToken}`
      }
    });

    if (!response.ok) {
      const errText = await response.text();
      console.log('❌ Failed to trash email');
      throw new Error(`Failed to trash email: ${response.status} - ${errText}`);
    }

    console.log('✅ Email moved to trash successfully');
    return await response.json();
  }

  // ----------------------------------------------
  // Star email
  // ----------------------------------------------
  static async starEmail(userId, messageId, accountId) {
    console.log('⭐ Starring email...');
    const currentUser = auth.currentUser;
    if (!currentUser || !userId) {
      throw new Error('No current user or userId found');
    }
    const idToken = await currentUser.getIdToken();

    const url = `${BASE_URL}/emails/${userId}/${messageId}/star`;
    const queryParams = new URLSearchParams();
    if (accountId) {
      queryParams.append('account_id', accountId);
    }

    const response = await fetch(`${url}?${queryParams.toString()}`, {
      method: 'POST',
      headers: {
        ...defaultHeaders,
        Authorization: `Bearer ${idToken}`
      }
    });

    if (!response.ok) {
      const errText = await response.text();
      console.log('❌ Failed to star email');
      throw new Error(`Failed to star email: ${response.status} - ${errText}`);
    }

    console.log('✅ Email starred successfully');
    return await response.json();
  }

  // ----------------------------------------------
  // Unstar email
  // ----------------------------------------------
  static async unstarEmail(userId, messageId, accountId) {
    console.log('☆ Unstarring email...');
    const currentUser = auth.currentUser;
    if (!currentUser || !userId) {
      throw new Error('No current user or userId found');
    }
    const idToken = await currentUser.getIdToken();

    const url = `${BASE_URL}/emails/${userId}/${messageId}/unstar`;
    const queryParams = new URLSearchParams();
    if (accountId) {
      queryParams.append('account_id', accountId);
    }

    const response = await fetch(`${url}?${queryParams.toString()}`, {
      method: 'POST',
      headers: {
        ...defaultHeaders,
        Authorization: `Bearer ${idToken}`
      }
    });

    if (!response.ok) {
      const errText = await response.text();
      console.log('❌ Failed to unstar email');
      throw new Error(`Failed to unstar email: ${response.status} - ${errText}`);
    }

    console.log('✅ Email unstarred successfully');
    return await response.json();
  }

  // ----------------------------------------------
  // Disconnect an email account (without deleting from Google)
  // ----------------------------------------------
  static async disconnectAccount(userId, accountId) {
    console.log(`Disconnecting account: ${accountId}`);
    
    if (!auth.currentUser || auth.currentUser.uid !== userId) {
      throw new Error('User not authorized');
    }
    
    try {
      const disconnectUrl = `${BASE_URL}/users/${userId}/accounts/${accountId}/disconnect`;
      
      const response = await fetch(disconnectUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await auth.currentUser.getIdToken()}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to disconnect account');
      }
      
      const data = await response.json();
      console.log('Account disconnected successfully:', data);
      return { success: true, message: data.message };
      
    } catch (error) {
      console.error('Error disconnecting account:', error);
      throw new Error(error.message || 'Failed to disconnect account. Please try again.');
    }
  }

  // ----------------------------------------------
  // Complete Gmail authorization after user grants access
  // ----------------------------------------------
  static async authorizeGmail(userId, code) {
    console.log('🔄 Completing Gmail authorization...');
    
    if (!userId || !code) {
      console.error('❌ Missing userId or authorization code');
      return { success: false, error: 'Missing required parameters' };
    }

    try {
      const url = `${BASE_URL}/oauth2/gmail/callback`;
      
      // Process the authorization code
      const response = await fetch(url, {
        method: 'POST',
        headers: defaultHeaders,
        body: JSON.stringify({
          user_id: userId,
          code: code
        })
      });
      
      let responseData;
      try {
        const text = await response.text();
        responseData = JSON.parse(text);
      } catch (e) {
        console.error('Failed to parse response:', e);
        responseData = { detail: 'Invalid server response' };
      }

      if (!response.ok) {
        console.error(`❌ Gmail authorization failed: ${response.status} - ${JSON.stringify(responseData)}`);
        
        // Handle specific error cases
        if (responseData.detail?.includes('invalid_grant')) {
          return { 
            success: false, 
            error: 'Authorization expired. Please try connecting your account again.' 
          };
        }
        
        if (responseData.detail?.includes('DatetimeWithNanoseconds')) {
          // This is a server error, we can retry getting the accounts
          const accountsData = await this.getAccounts(userId);
          if (accountsData.accounts?.length > 0) {
            // Account might have been added despite the error
            return {
              success: true,
              data: accountsData,
              accountAdded: true
            };
          }
        }

        return { 
          success: false, 
          error: 'Failed to connect Gmail account. Please try again.' 
        };
      }
      
      // Get the updated accounts list
      const accountsData = await this.getAccounts(userId);
      console.log('Current accounts:', accountsData);

      if (responseData.success) {
        const email = responseData.email;
        // Check if this email was actually added to the accounts list
        const isNewAccount = accountsData.accounts?.some(account => account.email === email);
        
        if (isNewAccount) {
          console.log('✅ New Gmail account added successfully:', email);
          return { 
            success: true, 
            data: accountsData,
            accountAdded: true,
            email: email
          };
        } else {
          console.log('⚠️ Email already connected:', email);
          return {
            success: true,
            accountAdded: false,
            error: `The Gmail account ${email} is already connected.`,
            data: accountsData
          };
        }
      } else {
        console.log('❌ Authorization response indicated failure');
        return {
          success: false,
          error: responseData.detail || 'Failed to connect Gmail account',
          data: accountsData
        };
      }
    } catch (error) {
      console.error('❌ Error in authorizeGmail:', error);
      return { 
        success: false, 
        error: 'An unexpected error occurred. Please try again.' 
      };
    }
  }

  // ----------------------------------------------
  // Send an email
  // ----------------------------------------------
  static async sendEmail({ user_id, to, subject, body, account_id, thread_id, message_id }) {
    console.log('📤 Sending email...');
    
    const currentUser = auth.currentUser;
    if (!currentUser || !user_id) {
      console.error('❌ No current user or user_id found');
      throw new Error('Authentication required. Please sign in and try again.');
    }

    try {
      const idToken = await currentUser.getIdToken();
      
      // Create email data
      const emailData = {
        user_id,
        to,
        subject,
        body,
        account_id,
        thread_id,
        message_id
      };

      // Send using Gmail API
      const response = await fetch(`${BASE_URL}/gmail/send`, {
        method: 'POST',
        headers: {
          ...defaultHeaders,
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify(emailData)
      });

      if (!response.ok) {
        const errText = await response.text();
        console.error('❌ Failed to send email:', response.status, errText);
        throw new Error(`Failed to send email: ${response.status} - ${errText}`);
      }

      const result = await response.json();
      console.log('✅ Email sent successfully');
      return result;
    } catch (error) {
      console.error('❌ Error in sendEmail:', error);
      throw error;
    }
  }

  // New method for payment confirmation emails
  static async sendPaymentConfirmationEmail(invoiceData, paymentDetails) {
    try {
      const settings = invoiceData.paymentEmailSettings || {};
      
      if (!settings.sendConfirmationEmail) {
        return { success: true, message: 'Confirmation emails disabled' };
      }

      // Send customer confirmation
      if (invoiceData.email) {
        await this.sendCustomerConfirmation(invoiceData, paymentDetails, settings);
      }

      // Send creator notification if enabled
      if (settings.notifyCreator && invoiceData.userId) {
        await this.sendCreatorNotification(invoiceData, paymentDetails);
      }

      return { success: true, message: 'Payment confirmation emails sent' };
    } catch (error) {
      console.error('Error sending payment confirmation emails:', error);
      throw error;
    }
  }

  static async sendCustomerConfirmation(invoiceData, paymentDetails, settings) {
    // Extract email from various possible fields
    let customerEmail = invoiceData.email || invoiceData.clientEmail;
    
    // If email contains name and email (e.g., "John Doe <john@example.com>"), extract just the email
    if (customerEmail && customerEmail.includes('<')) {
      const emailMatch = customerEmail.match(/<([^>]+)>/);
      if (emailMatch) {
        customerEmail = emailMatch[1];
      }
    }
    
    if (!customerEmail) {
      throw new Error('No customer email found for payment confirmation');
    }
    
    // Generate email content based on style
    const emailContent = this.generateCustomerEmailContent(invoiceData, paymentDetails, settings);
    
    return await this.sendEmail({
      user_id: invoiceData.userId,
      to: customerEmail,
      subject: `Payment Confirmed - ${invoiceData.project || 'Invoice'}`,
      body: emailContent,
      account_id: '' // Use default account
    });
  }

  static async sendCreatorNotification(invoiceData, paymentDetails) {
    const creatorEmail = await this.getCreatorEmail(invoiceData.userId);
    
    if (!creatorEmail) {
      console.log('Skipping creator notification - unable to get creator email');
      return { success: true, message: 'Creator notification skipped - no email available' };
    }
    
    const emailContent = this.generateCreatorEmailContent(invoiceData, paymentDetails);
    
    return await this.sendEmail({
      user_id: invoiceData.userId,
      to: creatorEmail,
      subject: `💰 Payment Received - ${invoiceData.project || 'Invoice'}`,
      body: emailContent,
      account_id: ''
    });
  }

  static generateCustomerEmailContent(invoiceData, paymentDetails, settings) {
    const clientName = invoiceData.client?.split(' ')[0] || 'there';
    const amount = (paymentDetails.amount / 100).toFixed(2);
    const date = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    // Generate message based on style
    let messageContent = '';
    switch (settings.emailMode) {
      case 'professional':
        messageContent = 'Your payment has been successfully processed. Thank you for your business.';
        break;
      case 'friendly':
        messageContent = 'Your payment went through perfectly! Thanks so much for working with us. 😊';
        break;
      case 'custom':
        messageContent = settings.customMessage || 'Your payment has been confirmed. Thank you!';
        break;
      default:
        messageContent = 'Your payment has been successfully processed. Thank you for your business.';
    }

    return `
      <div style="max-width: 600px; margin: 0 auto; font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', Roboto, sans-serif; background: #ffffff;">
        <!-- Header with Orange Gradient -->
        <div style="background: linear-gradient(135deg, #FFB27D 0%, #FFA164 100%); padding: 48px 32px; text-align: center; border-radius: 24px 24px 0 0;">
          <div style="background: rgba(0,0,0,0.1); width: 80px; height: 80px; border-radius: 20px; margin: 0 auto 24px; display: flex; align-items: center; justify-content: center;">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="color: rgba(0,0,0,0.8);">
              <path d="M20 6L9 17l-5-5"/>
            </svg>
          </div>
          <h1 style="color: #000; margin: 0; font-size: 32px; font-weight: 700; letter-spacing: -0.02em;">Payment Confirmed!</h1>
          <p style="color: rgba(0,0,0,0.7); margin: 12px 0 0; font-size: 18px; font-weight: 500;">Thank you for your payment</p>
        </div>
        
        <!-- Main Content -->
        <div style="background: #ffffff; padding: 48px 32px; border-radius: 0 0 24px 24px; box-shadow: 0 8px 32px rgba(0,0,0,0.08);">
          <p style="color: #1a1a1a; font-size: 18px; margin: 0 0 24px; line-height: 1.6;">
            Hi ${clientName},
          </p>
          
          <p style="color: #1a1a1a; font-size: 16px; margin: 0 0 32px; line-height: 1.7;">
            ${messageContent}
          </p>
          
          <!-- Payment Details Card -->
          <div style="background: linear-gradient(135deg, #f8f9fa 0%, #f1f3f4 100%); padding: 32px; border-radius: 20px; margin: 32px 0; border: 1px solid #e8eaed;">
            <h3 style="color: #1a1a1a; margin: 0 0 24px; font-size: 20px; font-weight: 600;">Payment Details</h3>
            <table style="width: 100%; border-collapse: collapse;">
              <tr>
                <td style="padding: 12px 0; color: #5f6368; font-weight: 500; font-size: 14px; text-transform: uppercase; letter-spacing: 0.05em;">Project</td>
                <td style="padding: 12px 0; color: #1a1a1a; text-align: right; font-weight: 600; font-size: 16px;">${invoiceData.project || 'Professional Services'}</td>
              </tr>
              <tr style="border-top: 1px solid #e8eaed;">
                <td style="padding: 12px 0; color: #5f6368; font-weight: 500; font-size: 14px; text-transform: uppercase; letter-spacing: 0.05em;">Amount Paid</td>
                <td style="padding: 12px 0; color: #137333; text-align: right; font-weight: 700; font-size: 20px;">$${amount}</td>
              </tr>
              <tr style="border-top: 1px solid #e8eaed;">
                <td style="padding: 12px 0; color: #5f6368; font-weight: 500; font-size: 14px; text-transform: uppercase; letter-spacing: 0.05em;">Payment Date</td>
                <td style="padding: 12px 0; color: #1a1a1a; text-align: right; font-weight: 500; font-size: 16px;">${date}</td>
              </tr>
              <tr style="border-top: 1px solid #e8eaed;">
                <td style="padding: 12px 0; color: #5f6368; font-weight: 500; font-size: 14px; text-transform: uppercase; letter-spacing: 0.05em;">Transaction ID</td>
                <td style="padding: 12px 0; color: #5f6368; text-align: right; font-family: 'SF Mono', Consolas, monospace; font-size: 14px;">${paymentDetails.id || 'N/A'}</td>
              </tr>
            </table>
          </div>
          
          <!-- Receipt Notice -->
          <div style="background: #e8f0fe; border: 1px solid #d2e3fc; padding: 20px; border-radius: 16px; margin: 32px 0;">
            <div style="display: flex; align-items: center; gap: 12px;">
              <div style="background: #1a73e8; width: 32px; height: 32px; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14,2 14,8 20,8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10,9 9,9 8,9"/>
                </svg>
              </div>
              <div>
                <p style="color: #1a73e8; margin: 0; font-weight: 600; font-size: 14px;">Receipt Included</p>
                <p style="color: #5f6368; margin: 4px 0 0; font-size: 13px;">Keep this email for your records</p>
              </div>
            </div>
          </div>
          
          <p style="color: #5f6368; font-size: 14px; margin: 40px 0 0; padding-top: 24px; border-top: 1px solid #e8eaed; text-align: center;">
            This is an automated confirmation from 
            <a href="https://klorah.app" style="color: #FFB27D; text-decoration: none; font-weight: 600;">Klorah</a>
          </p>
        </div>
      </div>
    `;
  }

  static generateCreatorEmailContent(invoiceData, paymentDetails) {
    const amount = (paymentDetails.amount / 100).toFixed(2);
    const clientName = invoiceData.client || 'your client';
    
    return `
      <div style="max-width: 600px; margin: 0 auto; font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', Roboto, sans-serif;">
        <!-- Header -->
        <div style="background: linear-gradient(135deg, #059669 0%, #047857 100%); padding: 48px 32px; text-align: center; border-radius: 24px 24px 0 0;">
          <div style="background: rgba(255,255,255,0.15); width: 80px; height: 80px; border-radius: 20px; margin: 0 auto 24px; display: flex; align-items: center; justify-content: center;">
            <span style="font-size: 40px;">💰</span>
          </div>
          <h1 style="color: white; margin: 0; font-size: 32px; font-weight: 700;">Payment Received!</h1>
        </div>
        
        <!-- Content -->
        <div style="background: white; padding: 48px 32px; border-radius: 0 0 24px 24px; box-shadow: 0 8px 32px rgba(0,0,0,0.08);">
          <p style="color: #1a1a1a; font-size: 18px; margin: 0 0 24px;">
            Great news! Your invoice has been paid.
          </p>
          
          <div style="background: #f0fdf4; border: 2px solid #bbf7d0; padding: 32px; border-radius: 20px; margin: 32px 0; text-align: center;">
            <h2 style="color: #065f46; margin: 0 0 12px; font-size: 16px; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 600;">Amount Received</h2>
            <p style="color: #065f46; margin: 0; font-size: 36px; font-weight: 800;">$${amount}</p>
            <p style="color: #059669; margin: 12px 0 0; font-size: 16px; font-weight: 500;">From ${clientName}</p>
          </div>
          
          <div style="background: #f8f9fa; padding: 24px; border-radius: 16px; margin: 32px 0;">
            <h3 style="color: #1a1a1a; margin: 0 0 16px; font-size: 18px; font-weight: 600;">Project Details</h3>
            <p style="color: #5f6368; margin: 0; font-size: 16px; font-weight: 500;">${invoiceData.project || 'Professional Services'}</p>
          </div>
          
          <p style="color: #5f6368; font-size: 14px; margin: 32px 0 0; text-align: center;">
            Funds will be transferred to your connected account within 2-7 business days.
          </p>
        </div>
      </div>
    `;
  }

  static async getCreatorEmail(userId) {
    try {
      // Get user data from Firebase Auth via our server
      const response = await fetch(`${this.BASE_URL}/get-user-email/${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.email;
      }
      
      // Fallback: try to get from current auth state if available
      const auth = getAuth();
      if (auth.currentUser && auth.currentUser.uid === userId) {
        return auth.currentUser.email;
      }
      
      throw new Error('Unable to get creator email');
    } catch (error) {
      console.error('Error getting creator email:', error);
      // Return null so email won't be sent if we can't get the email
      return null;
    }
  }

  // New method for triggering payment confirmation emails via server
  static async triggerPaymentConfirmationEmails(invoiceId) {
    try {
      console.log('🔄 Triggering payment confirmation emails for invoice:', invoiceId);
      
      if (!invoiceId) {
        throw new Error('Invoice ID is required');
      }
      
      const response = await fetch(`${BASE_URL}/send-payment-confirmation-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          invoice_id: invoiceId
        })
      });

      if (!response.ok) {
        let errorMessage = 'Failed to send payment confirmation emails';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (parseError) {
          // If we can't parse the error response, use the status text
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('✅ Payment confirmation emails triggered successfully:', result);
      return result;
    } catch (error) {
      console.error('❌ Error triggering payment confirmation emails:', error);
      throw error;
    }
  }

  // Test method to check if server endpoint is available
  static async testPaymentEmailEndpoint() {
    try {
      console.log('🧪 Testing payment email endpoint availability...');
      
      const response = await fetch(`${BASE_URL}/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const isHealthy = response.ok;
      console.log(`Server health check: ${isHealthy ? '✅ Available' : '❌ Unavailable'}`);
      
      return {
        serverAvailable: isHealthy,
        endpointUrl: `${BASE_URL}/send-payment-confirmation-email`,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('❌ Server health check failed:', error);
      return {
        serverAvailable: false,
        error: error.message,
        endpointUrl: `${BASE_URL}/send-payment-confirmation-email`,
        timestamp: new Date().toISOString()
      };
    }
  }

  // ----------------------------------------------
  // Invoice Reminder Functionality
  // ----------------------------------------------
  static async sendInvoiceReminder(invoiceData, userAccounts) {
    try {
      console.log('📧 Sending invoice reminder...');
      
      // Extract customer email
      let customerEmail = invoiceData.email || invoiceData.clientEmail;
      
      // If email contains name and email format, extract just the email
      if (customerEmail && customerEmail.includes('<')) {
        const emailMatch = customerEmail.match(/<([^>]+)>/);
        if (emailMatch) {
          customerEmail = emailMatch[1];
        }
      }
      
      if (!customerEmail) {
        throw new Error('No customer email found for invoice reminder');
      }

      // Generate invoice URL
      const invoiceUrl = `https://klorah.app/preview-invoice/${invoiceData.id}`;
      
      // Calculate days overdue if past due date
      let overdueText = '';
      if (invoiceData.dueDate) {
        const dueDate = new Date(invoiceData.dueDate);
        const today = new Date();
        const daysOverdue = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));
        
        if (daysOverdue > 0) {
          overdueText = `This invoice is ${daysOverdue} day${daysOverdue > 1 ? 's' : ''} overdue. `;
        }
      }

      // Format amount
      const formattedAmount = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: invoiceData.currency || 'USD'
      }).format(invoiceData.amount || 0);

      // Create reminder email body
      const emailBody = `
        <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
          <h2 style="color: #333; margin-bottom: 20px;">Payment Reminder</h2>
          
          <p style="color: #666; line-height: 1.6; margin-bottom: 15px;">
            Hi ${invoiceData.clientName || invoiceData.client || 'there'},
          </p>
          
          <p style="color: #666; line-height: 1.6; margin-bottom: 15px;">
            This is a friendly reminder that invoice <strong>${invoiceData.project || 'Invoice'}</strong> 
            for <strong>${formattedAmount}</strong> is still pending payment.
          </p>
          
          ${overdueText ? `<p style="color: #d9534f; font-weight: 500; margin-bottom: 15px;">${overdueText}Please arrange payment at your earliest convenience.</p>` : ''}
          
          <p style="color: #666; line-height: 1.6; margin-bottom: 25px;">
            You can view and pay your invoice by clicking the button below:
          </p>
          
          <div style="text-align: center; margin: 30px 0;">
            <a href="${invoiceUrl}" style="display: inline-block; background-color: #FFB27D; color: black; padding: 12px 30px; text-decoration: none; border-radius: 8px; font-weight: 500; font-size: 16px;">
              View & Pay Invoice
            </a>
          </div>
          
          <p style="color: #666; line-height: 1.6; margin-bottom: 15px;">
            If you have already made payment, please disregard this reminder. If you have any questions or concerns, 
            please don't hesitate to reach out.
          </p>
          
          <p style="color: #666; line-height: 1.6; margin-bottom: 15px;">
            Thank you for your prompt attention to this matter.
          </p>
          
          <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #eee;">
            <p style="color: #999; font-size: 12px; margin: 0;">
              Best regards,<br>
              ${invoiceData.fromName || invoiceData.businessInfo?.businessName || 'Your Service Provider'}
            </p>
            <p style="color: #999; font-size: 11px; margin-top: 10px;">
              Sent with <a href="https://klorah.app" style="color: #FFB27D; text-decoration: none;">Klorah</a>
            </p>
          </div>
        </div>
      `;

      // Use the first available account or empty string for default
      const accountId = userAccounts && userAccounts.length > 0 ? userAccounts[0].accountId : '';

      // Send the reminder email
      const result = await this.sendEmail({
        user_id: invoiceData.userId,
        to: customerEmail,
        subject: `Payment Reminder: ${invoiceData.project || 'Invoice'} - ${formattedAmount}`,
        body: emailBody,
        account_id: accountId
      });

      console.log('✅ Invoice reminder sent successfully');
      return result;
      
    } catch (error) {
      console.error('❌ Error sending invoice reminder:', error);
      throw error;
    }
  }

  // ----------------------------------------------
  // Generate AI Draft for Reply (Ghost Text)
  // ----------------------------------------------
  static async generateAIDraft({
    email_id,
    subject,
    sender,
    sender_email,
    content,
    thread_messages = [],
    user_name = '',
    user_email = '',
    tone = 'professional',
    formality_level = 'balanced',
    formal_signature = '',
    casual_signature = '',
    has_signature = false
  }) {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      const idToken = await currentUser.getIdToken();

      // Select appropriate signature based on formality
      const signature = formality_level === 'casual' && casual_signature
        ? casual_signature
        : formal_signature || '';

      const response = await fetch(`${BASE_URL}/ai/generate-draft`, {
        method: 'POST',
        headers: {
          ...defaultHeaders,
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({
          email_id,
          subject,
          sender,
          sender_email,
          content,
          thread_messages,
          user_name,
          user_email,
          tone,
          formality_level,
          formal_signature,
          casual_signature,
          signature,
          has_signature
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate AI draft');
      }

      const data = await response.json();
      return {
        success: true,
        draft: data.draft || '',
        tone: data.tone || tone
      };
    } catch (error) {
      console.error('Error generating AI draft:', error);
      throw error;
    }
  }

  // ----------------------------------------------
  // Ghost Text Autocomplete Suggestion
  // ----------------------------------------------
  static async getGhostTextSuggestion({
    currentText,
    subject = '',
    recipient = '',
    context = '',
    isReply = false,
    writingStyle = ''
  }) {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      return { suggestion: '' };
    }

    // Don't suggest for very short text
    if (!currentText || currentText.trim().length < 10) {
      return { suggestion: '' };
    }

    try {
      const idToken = await currentUser.getIdToken();

      const response = await fetch(`${BASE_URL}/ai/ghost-text`, {
        method: 'POST',
        headers: {
          ...defaultHeaders,
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({
          current_text: currentText,
          subject: subject || '',
          recipient: recipient || '',
          context: context || '',
          is_reply: isReply || false,
          writing_style: writingStyle || '',
        })
      });

      if (!response.ok) {
        // Ghost text is optional - fail silently
        return { suggestion: '' };
      }

      const data = await response.json();
      return { suggestion: data.suggestion || '' };
    } catch (error) {
      // Ghost text is optional - fail silently
      console.log('Ghost text suggestion failed (optional):', error.message);
      return { suggestion: '' };
    }
  }

  static async getContacts(userId, accountId, query = '') {
    console.log('👥 Fetching contacts...');

    const currentUser = auth.currentUser;
    if (!currentUser || !userId) {
      console.log('❌ No current user or userId found');
      return { contacts: [] };
    }

    try {
      const idToken = await currentUser.getIdToken();

      const queryParams = new URLSearchParams();
      if (accountId) {
        queryParams.append('account_id', accountId);
      }
      if (query) {
        queryParams.append('query', query);
      }
      queryParams.append('limit', '50');

      const url = `${BASE_URL}/contacts/${userId}?${queryParams.toString()}`;
      console.log('🌐 Fetching contacts from:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          ...defaultHeaders,
          Authorization: `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        console.log('❌ Failed to fetch contacts:', response.status);
        return { contacts: [] };
      }

      const data = await response.json();
      console.log('✅ Successfully fetched contacts:', data.contacts?.length || 0);
      return data;
    } catch (error) {
      console.error('❌ Error fetching contacts:', error);
      return { contacts: [] };
    }
  }
}

export default EmailService;